import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { shipmentsService } from '../services/logistics/shipments';
import { clientsService } from '../services/logistics/clients';
import { externalDriversService } from '../services/logistics/external-drivers';
import { invoicesService } from '../services/accounting/invoices';
import { expensesService } from '../services/accounting/expenses';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import {
  TruckIcon,
  UsersIcon,
  DocumentTextIcon,
  ClockIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { getMonthWithFallback } from '../utils/monthTranslations';

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const { userProfile, isSuperAdmin, currentCompany } = useAuth();

  // Force re-render when language changes
  const currentLang = i18n.language;
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    shipments: [],
    clients: [],
    statistics: {
      totalShipments: 0,
      activeShipments: 0,
      totalClients: 0,
      pendingDeliveries: 0,
      deliveredShipments: 0,
      inTransitShipments: 0,
      canceledShipments: 0
    }
  });

  // Force translation reload when language changes
  useEffect(() => {
    i18n.reloadResources(currentLang);
  }, [currentLang, i18n]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!currentCompany?.id) {
        setLoading(false);
        return;
      }

      try {
        // Fetch real data from Firebase
        const [shipmentsData, clientsData, invoicesData, expensesData] = await Promise.all([
          shipmentsService.getShipments(currentCompany.id),
          clientsService.getClients(currentCompany.id),
          invoicesService.getInvoices(currentCompany.id),
          expensesService.getExpenses(currentCompany.id)
        ]);

        // Calculate real statistics
        const externalShipments = shipmentsData.filter(s => s.isExternalDriver);
        const internalShipments = shipmentsData.filter(s => !s.isExternalDriver);
        const externalShipmentsThisMonth = externalShipments.filter(s => {
          const shipmentDate = s.createdAt?.toDate ? s.createdAt.toDate() : new Date(s.createdAt);
          const now = new Date();
          return shipmentDate.getMonth() === now.getMonth() && shipmentDate.getFullYear() === now.getFullYear();
        });
        const totalShipmentsThisMonth = shipmentsData.filter(s => {
          const shipmentDate = s.createdAt?.toDate ? s.createdAt.toDate() : new Date(s.createdAt);
          const now = new Date();
          return shipmentDate.getMonth() === now.getMonth() && shipmentDate.getFullYear() === now.getFullYear();
        });

        const statistics = {
          totalShipments: shipmentsData.length,
          activeShipments: shipmentsData.filter(s => s.status === 'in_transit').length,
          totalClients: clientsData.length,
          pendingDeliveries: shipmentsData.filter(s => s.status === 'pending').length,
          deliveredShipments: shipmentsData.filter(s => s.status === 'delivered').length,
          inTransitShipments: shipmentsData.filter(s => s.status === 'in_transit').length,
          canceledShipments: shipmentsData.filter(s => s.status === 'canceled').length,
          externalShipments: externalShipments.length,
          externalShipmentsThisMonth: externalShipmentsThisMonth.length,
          totalShipmentsThisMonth: totalShipmentsThisMonth.length,
          externalShipmentsPercentage: totalShipmentsThisMonth.length > 0
            ? Math.round((externalShipmentsThisMonth.length / totalShipmentsThisMonth.length) * 100)
            : 0
        };

        setDashboardData({
          shipments: shipmentsData,
          clients: clientsData,
          invoices: invoicesData,
          expenses: expensesData,
          statistics
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [currentCompany?.id]);

  // Redirect Super Admin to their dashboard
  if (isSuperAdmin()) {
    return <Navigate to="/super-admin/dashboard" replace />;
  }

  // Calculate real revenue data from last 6 months including current month
  const getRevenueData = () => {
    const now = new Date();
    const monthsData = [];

    // Generate last 6 months including current month
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = getMonthWithFallback(date, i18n.language, t);

      // Calculate revenue from invoices for this month
      const monthRevenue = (dashboardData.invoices || [])
        .filter(invoice => {
          const invoiceDate = invoice.issueDate?.toDate ? invoice.issueDate.toDate() : new Date(invoice.issueDate);
          return invoiceDate.getMonth() === date.getMonth() &&
            invoiceDate.getFullYear() === date.getFullYear();
        })
        .reduce((sum, invoice) => sum + (invoice.finalTotal || invoice.total || 0), 0);

      // Calculate expenses for this month
      const monthExpenses = (dashboardData.expenses || [])
        .filter(expense => {
          const expenseDate = expense.date?.toDate ? expense.date.toDate() : new Date(expense.date);
          return expenseDate.getMonth() === date.getMonth() &&
            expenseDate.getFullYear() === date.getFullYear();
        })
        .reduce((sum, expense) => sum + (expense.amount || 0), 0);

      monthsData.push({
        month: monthName,
        revenue: monthRevenue,
        expenses: monthExpenses
      });
    }

    return monthsData;
  };

  const revenueData = getRevenueData();

  // Real shipment status data
  const shipmentStatusData = [
    {
      name: t('shipments.delivered'),
      value: dashboardData.statistics?.deliveredShipments || 0,
      color: '#10b981'
    },
    {
      name: t('shipments.in_transit'),
      value: dashboardData.statistics?.inTransitShipments || 0,
      color: '#3b82f6'
    },
    {
      name: t('shipments.pending'),
      value: dashboardData.statistics?.pendingDeliveries || 0,
      color: '#f59e0b'
    },
    {
      name: t('shipments.canceled'),
      value: dashboardData.statistics?.canceledShipments || 0,
      color: '#ef4444'
    },
  ];

  // External vs Internal shipments data
  const externalVsInternalData = [
    {
      name: t('dashboard.internal_shipments'),
      value: (dashboardData.statistics?.totalShipments || 0) - (dashboardData.statistics?.externalShipments || 0),
      color: '#3b82f6'
    },
    {
      name: t('dashboard.external_shipments_short'),
      value: dashboardData.statistics?.externalShipments || 0,
      color: '#f59e0b'
    }
  ];

  // Calculate real route data from actual shipments
  const getTopRoutes = () => {
    if (!dashboardData.shipments || dashboardData.shipments.length === 0) {
      return [];
    }

    // Group shipments by route (origin -> destination)
    const routeMap = new Map();

    dashboardData.shipments.forEach(shipment => {
      const route = `${shipment.origin} → ${shipment.destination}`;

      if (!routeMap.has(route)) {
        routeMap.set(route, {
          route,
          shipments: 0,
          revenue: 0
        });
      }

      const routeData = routeMap.get(route);
      routeData.shipments += 1;

      // Calculate revenue from related invoices
      const relatedInvoices = (dashboardData.invoices || []).filter(invoice =>
        invoice.shipmentIds && invoice.shipmentIds.includes(shipment.id)
      );

      const shipmentRevenue = relatedInvoices.reduce((sum, invoice) =>
        sum + (invoice.finalTotal || invoice.total || 0), 0
      );

      routeData.revenue += shipmentRevenue;
    });

    // Convert to array and sort by revenue (descending)
    return Array.from(routeMap.values())
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5); // Top 5 routes
  };

  const routeData = getTopRoutes();

  // Real KPIs for logistics tenant
  const operationalKPIs = [
    {
      title: t('dashboard.active_shipments'),
      value: (dashboardData.statistics?.activeShipments || 0).toString(),
      change: '0%',
      changeType: 'neutral',
      icon: TruckIcon,
      color: 'blue',
      description: t('dashboard.shipments_in_progress')
    },
    {
      title: t('dashboard.total_clients'),
      value: (dashboardData.statistics?.totalClients || 0).toString(),
      change: '0%',
      changeType: 'neutral',
      icon: UsersIcon,
      color: 'green',
      description: t('dashboard.active_business_clients')
    },
    {
      title: t('dashboard.total_shipments'),
      value: (dashboardData.statistics?.totalShipments || 0).toString(),
      change: '0%',
      changeType: 'neutral',
      icon: TruckIcon,
      color: 'green',
      description: t('dashboard.shipments_in_progress')
    },
    {
      title: t('dashboard.external_shipments') || 'External Shipments',
      value: (dashboardData.statistics?.externalShipmentsThisMonth || 0).toString(),
      change: `${dashboardData.statistics?.externalShipmentsPercentage || 0}%`,
      changeType: (dashboardData.statistics?.externalShipmentsPercentage || 0) > 50 ? 'increase' : 'neutral',
      icon: ClockIcon,
      color: 'orange',
      description: t('dashboard.external_shipments_percentage') || 'External Shipments %'
    },
  ];

  const quickActions = [
    {
      title: t('dashboard.new_shipment'),
      description: t('dashboard.create_new_shipment'),
      href: '/shipments/create',
      icon: TruckIcon,
      color: 'blue'
    },
    {
      title: t('dashboard.add_client'),
      description: t('dashboard.register_new_client'),
      href: '/clients/create',
      icon: UsersIcon,
      color: 'green'
    },
    {
      title: t('dashboard.create_invoice'),
      description: t('dashboard.generate_new_invoice'),
      href: '/accounting?tab=invoices',
      icon: DocumentTextIcon,
      color: 'purple'
    }
  ];

  // Real recent activities based on actual shipments
  const getRecentActivities = () => {
    if (!dashboardData.shipments || dashboardData.shipments.length === 0) {
      return [];
    }

    const recentShipments = dashboardData.shipments
      .sort((a, b) => {
        const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return bDate - aDate;
      })
      .slice(0, 4);

    return recentShipments.map((shipment, index) => ({
      id: shipment.id,
      type: 'shipment',
      title: `Shipment ${shipment.trackingNumber} created`,
      description: `${shipment.origin} → ${shipment.destination}`,
      time: getTimeAgo(shipment.createdAt),
      icon: TruckIcon,
      color: shipment.status === 'delivered' ? 'green' :
        shipment.status === 'in_transit' ? 'blue' : 'orange'
    }));
  };

  const getTimeAgo = (timestamp) => {
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 60) {
      return `${diffInMinutes} minutes ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  const KPICard = ({ title, value, change, changeType, icon: Icon, color, description }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600 border-blue-200',
      green: 'bg-green-50 text-green-600 border-green-200',
      orange: 'bg-orange-50 text-orange-600 border-orange-200',
      red: 'bg-red-50 text-red-600 border-red-200',
      purple: 'bg-purple-50 text-purple-600 border-purple-200',
    };

    return (
      <div className="card hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center">
              <div className="flex items-center space-x-3 rtl:space-x-reverse flex-1">
                <div className={`p-3 rounded-lg border flex-shrink-0 ${colorClasses[color]}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-600">{title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
                </div>
              </div>
              <div className={`flex items-center text-sm font-medium flex-shrink-0 mr-4 rtl:ml-4 rtl:mr-0 ${changeType === 'increase' ? 'text-green-600' :
                changeType === 'decrease' ? 'text-red-600' : 'text-gray-500'
                }`}>
                {changeType === 'increase' ? (
                  <ArrowUpIcon className="h-4 w-4 mr-1 rtl:ml-1 rtl:mr-0" />
                ) : changeType === 'decrease' ? (
                  <ArrowDownIcon className="h-4 w-4 mr-1 rtl:ml-1 rtl:mr-0" />
                ) : null}
                <span>{change}</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3">{description}</p>
          </div>
        </div>
      </div>
    );
  };

  const QuickActionCard = ({ title, description, href, icon: Icon, color }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600 hover:bg-blue-100',
      green: 'bg-green-50 text-green-600 hover:bg-green-100',
      purple: 'bg-purple-50 text-purple-600 hover:bg-purple-100',
    };

    return (
      <a
        href={href}
        className="card hover:shadow-md transition-all duration-200 hover:scale-105 block"
      >
        <div className="flex items-center space-x-3 rtl:space-x-reverse">
          <div className={`p-3 rounded-lg transition-colors flex-shrink-0 ${colorClasses[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-600">{description}</p>
          </div>
          <PlusIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
        </div>
      </a>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('dashboard.title')}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('dashboard.welcome_back')}, {userProfile?.name} - {currentCompany?.name || t('dashboard.logistics_company')}
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              {t('dashboard.online')}
            </span>
            <span className="text-sm text-gray-500">
              {t('dashboard.last_updated')}: {new Date().toLocaleTimeString(i18n.language === 'ar' ? 'ar-SA' : 'en-US')}
            </span>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t('dashboard.key_metrics')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {operationalKPIs.map((kpi, index) => (
            <KPICard
              key={index}
              title={kpi.title}
              value={kpi.value}
              change={kpi.change}
              changeType={kpi.changeType}
              icon={kpi.icon}
              color={kpi.color}
              description={kpi.description}
            />
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t('dashboard.quick_actions')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {quickActions.map((action, index) => (
            <QuickActionCard
              key={index}
              title={action.title}
              description={action.description}
              href={action.href}
              icon={action.icon}
              color={action.color}
            />
          ))}
        </div>
      </div>

      {/* Charts Section - Show if there's any data */}
      {(dashboardData.shipments.length > 0 || dashboardData.invoices?.length > 0 || dashboardData.expenses?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Chart */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('dashboard.revenue_trend')}
              </h3>
              <p className="text-sm text-gray-500">
                {t('dashboard.last_6_months') || 'Last 6 Months (Including Current)'}
              </p>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ 
                      fontSize: 12,
                      fontFamily: i18n.language === 'ar' ? 'Amiri, Arial, sans-serif' : 'Arial, sans-serif',
                      textAnchor: i18n.language === 'ar' ? 'end' : 'middle'
                    }}
                  />
                  <YAxis />
                  <Tooltip
                    formatter={(value, name) => [
                      `SAR ${value.toLocaleString('en-US')}`,
                      name === 'revenue' ? t('dashboard.revenue') : t('dashboard.expenses')
                    ]}
                  />
                  <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} />
                  <Line type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* External vs Internal Shipments */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('dashboard.internal_vs_external') || 'Internal vs External'}
              </h3>
              <p className="text-sm text-gray-500">
                {t('dashboard.current_month') || 'Current Month'}
              </p>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={externalVsInternalData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    dataKey="value"
                    label={false}
                  >
                    {externalVsInternalData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              {/* Legend outside the chart */}
              <div className="flex justify-center space-x-4 rtl:space-x-reverse mt-4">
                {externalVsInternalData.map((entry, index) => (
                  <div key={index} className="flex items-center space-x-2 rtl:space-x-reverse">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: entry.color }}
                    ></div>
                    <span className="text-sm text-gray-600">
                      {entry.name || (index === 0 ? 'Internal Shipments' : 'External Shipments')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Routes & Recent Activities - Show if there's shipment data */}
      {dashboardData.shipments.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Routes */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('dashboard.top_routes')}
              </h3>
              <p className="text-sm text-gray-500">
                {t('dashboard.most_profitable_routes')}
              </p>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                {routeData.length > 0 ? (
                  routeData.map((route, index) => (
                    <div key={index} className="flex items-center justify-between py-2">
                      <div>
                        <p className="font-medium text-gray-900">{route.route}</p>
                        <p className="text-sm text-gray-500">
                          {route.shipments} {t('dashboard.shipments')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">
                          SAR {route.revenue.toLocaleString('en-US')}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">{t('dashboard.no_data_available')}</p>
                )}
              </div>
            </div>
          </div>

          {/* Recent Activities */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('dashboard.recent_activities')}
              </h3>
              <p className="text-sm text-gray-500">
                {t('dashboard.latest_updates')}
              </p>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                {getRecentActivities().length > 0 ? (
                  getRecentActivities().map((activity) => {
                    const Icon = activity.icon;
                    return (
                      <div key={activity.id} className="flex items-start space-x-3 rtl:space-x-reverse">
                        <div className={`p-2 rounded-lg ${activity.color === 'blue' ? 'bg-blue-50 text-blue-600' :
                          activity.color === 'green' ? 'bg-green-50 text-green-600' :
                            'bg-gray-50 text-gray-600'
                          }`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {activity.title}
                          </p>
                          <p className="text-sm text-gray-500">
                            {activity.description}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {activity.time}
                          </p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-500 text-center py-4">{t('dashboard.no_data_available')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 